import { Key, useCallback, useState } from 'react'
import { Popup } from 'components/Popup'
import { Button, BUTTON_TYPES } from 'components/Button'
import { Typography } from 'components/Typography'
import { Table } from 'components/Table'
import { BTN_TXT } from 'constants/txt'
import { PendingCategory } from 'components/Forms/forms.user.d'
import { useCRUDUserPopupProviderContext } from '../Provider/CRUDUserPopupProvider'
import { columns } from './columns'

type ReviewAndApproveCategoriesPopupProps = {
  userName: string
  pendingCategories: PendingCategory[]
  onArchive: () => void
  onApproveCategories: (categories: string[]) => void
}

export const ReviewAndApproveCategoriesPopup = (
  props: ReviewAndApproveCategoriesPopupProps
) => {
  const { pendingCategories, userName, onArchive, onApproveCategories } = props

  const {
    reviewAndApproveCategoriesPopup: {
      actions: { close },
      state: { visible, title }
    },
    activateWithoutCategoriesPopup
  } = useCRUDUserPopupProviderContext()

  const [selectedCategories, setSelectedCategories] = useState<string[]>([])

  const handleSelectRow = useCallback((keys: Key[]) => {
    setSelectedCategories(keys as string[])
  }, [])

  const onCancel = useCallback(() => {
    setSelectedCategories([])
    close()
  }, [close])

  const onSubmit = useCallback(() => {
    if (!selectedCategories.length) {
      onCancel()
      activateWithoutCategoriesPopup.actions.open()
    } else {
      onApproveCategories(selectedCategories)
    }
  }, [
    activateWithoutCategoriesPopup.actions,
    onApproveCategories,
    onCancel,
    selectedCategories
  ])

  return (
    <Popup
      visible={visible}
      onCancel={onCancel}
      title={title}
      width={670}
      footer={
        <div className="full-width row gap-8 justify-between">
          <Button
            type={BUTTON_TYPES.DEFAULT}
            onClick={onArchive}
            upperCase
            danger
          >
            {BTN_TXT.ARCHIVE_USER}
          </Button>
          <div className="row">
            <Button onClick={onCancel} type={BUTTON_TYPES.DEFAULT} upperCase>
              {BTN_TXT.CANCEL}
            </Button>
            <Button type={BUTTON_TYPES.PRIMARY} onClick={onSubmit} upperCase>
              {BTN_TXT.SUBMIT}
            </Button>
          </div>
        </div>
      }
    >
      <Typography.Body2 className="grey-text">
        {userName} selected these categories during registration. Choose the
        categories to approve. All categories can be managed from {userName}'s
        profile.
      </Typography.Body2>
      <Table<PendingCategory>
        className="review-and-approve-categories-table mt-16"
        dataSource={pendingCategories}
        columns={columns}
        rowKey="uuid"
        scroll={{ x: 'max-content' }}
        rowSelection={{
          selectedRowKeys: selectedCategories,
          onChange: handleSelectRow
        }}
      />
    </Popup>
  )
}
